import { useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";

import { confirmQuotation, editQuotation } from "api";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { useGetQuotations } from "api/queries/quotes/useGetQuotations";
import Button from "components/v2/Button/Button";
import Dropdown from "components/v2/Dropdown/Dropdown";
import useConfirm from "hooks/useConfirm";
import errorToast from "helpers/errorToast";
import { modifyDraftData } from "helpers/quotationFormHelper";
import CancelDialog from "pages/provider/QuotationDetail/CancelDialog";
import EditScheduleDialog from "pages/provider/QuotationDetail/EditScheduleDialog";
import ExtendQuoteDialog from "pages/provider/QuotationDetail/ExtendQuoteDialog";
import QuotationDuplicateDialog from "pages/provider/QuotationDetail/QuotationDuplicateDialog";

interface QuotationFooterActionsProps {
  quotation: any;
}

export default function QuotationFooterActions({ quotation }: QuotationFooterActionsProps) {
  const intl = useIntl();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { confirm, ConfirmModal } = useConfirm();
  const { reset } = useForm();

  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [showEditScheduleDialog, setShowEditScheduleDialog] = useState(false);
  const [draftId, setDraftId] = useState("");

  const { data: shipmentData }: any = useGetShipmentBasic(id!, {
    enabled: !!id,
    refetchOnWindowFocus: true,
  });

  const { refetch: refetchQuotationLists, isLoading: isLoadingQuotationLists } = useGetQuotations({
    shipmentId: id!,
    options: {
      enabled: !!id,
    },
  });

  const { status, created_on_newui, general, reference } = quotation ?? {};

  const pendingExtension = general?.extension_requested_at && general?.extension_requested_by;

  const canEditOnDuplicateOnNewUI = general?.is_rate_card_quote || created_on_newui;
  const isNotBookedAsDuplicate = !["booked_as_duplicate"].includes(status);
  const isExpired = shipmentData?.organization?.quoting_deadline_seconds_remaining <= 0;

  // Buttons outside kebab
  const showRevalidateQuoteButton = ["confirmation_needed"].includes(status);
  const showExtendQuoteButton = (isNotBookedAsDuplicate && isExpired) || pendingExtension;

  // Kebab menu options visibility
  const showEditQuoteOrSchedule =
    ["booked", "submitted", "open_to_book", "confirmation_needed"].includes(status) && !isExpired;
  const showExtendQuote = !showExtendQuoteButton && isNotBookedAsDuplicate && !pendingExtension;
  const showDuplicateQuote =
    ["booked_as_duplicate", "booked", "submitted", "open_to_book", "confirmation_needed"].includes(status) &&
    !isExpired &&
    canEditOnDuplicateOnNewUI;
  const showCancelQuote = isNotBookedAsDuplicate;
  const isDetailDrawerOpen = searchParams.get("detail") !== null;

  const handleClickEdit = async () => {
    if (!created_on_newui && !general?.is_rate_card_quote) {
      await confirm({
        title: intl.formatMessage({
          id: "quote.submitOldUIAlertTitle",
          defaultMessage: "This quote was submitted on the Old UI",
        }),
        message: intl.formatMessage({
          id: "quote.submitOldUIAlertMessage",
          defaultMessage: "Please cancel the quote and re-submit the quote on the Next UI.",
        }),
        disableCancel: true,
      });
      return;
    }

    editQuotation(reference)
      .then((res: any) => {
        const existingData = { ...res.data.data };
        const updatedData = {
          ...existingData,
          schedule: {
            ...existingData.schedule,
            ...quotation.schedule,
          },
        };
        reset(modifyDraftData(updatedData));
        setDraftId(res.data.reference_number);
        if (general.is_rate_card_quote) {
          setShowEditScheduleDialog(true);
        } else {
          navigate(`/shipment/${id}/quotation/add?active_step=service&draft_id=${res.data.reference_number}`);
        }
      })
      .catch((err) => errorToast(err));
  };

  const { mutate: confirmQuotationMutation, isLoading: isConfirmingQuotation } = useMutation({
    mutationFn: () => confirmQuotation(reference),
    onSuccess: () => {
      refetchQuotationLists();
      toast(
        intl.formatMessage({
          id: "quote.confirmSuccessMsg",
          defaultMessage: "Quote confirmed successfully",
        }),
        { type: "success" },
      );
    },
    onError: (error) => {
      errorToast(error);
    },
  });

  const handleClickRevalidate = () => {
    confirmQuotationMutation();
  };

  const kebabMenuOptions = [
    ...(showEditQuoteOrSchedule
      ? [
          {
            value: "edit",
            label: intl.formatMessage({ id: "general.edit", defaultMessage: "Edit" }),
            onClick: handleClickEdit,
          },
        ]
      : []),
    ...(showExtendQuote
      ? [
          {
            value: "extend",
            label: intl.formatMessage({ id: "quote.quotation.extendValidity", defaultMessage: "Extend validatity" }),
            onClick: () => setShowExtendDialog(true),
          },
        ]
      : []),
    ...(showDuplicateQuote
      ? [
          {
            value: "duplicate",
            label: intl.formatMessage({ id: "general.duplicate", defaultMessage: "Duplicate" }),
            onClick: () => setShowDuplicateDialog(true),
          },
        ]
      : []),
    ...(showCancelQuote
      ? [
          {
            value: "cancel",
            label: intl.formatMessage({ id: "quote.cancelQuote", defaultMessage: "Cancel quote" }),
            onClick: () => setShowCancelDialog(true),
            variant: "error" as const,
          },
        ]
      : []),
  ];

  return (
    <>
      <ConfirmModal />
      <CancelDialog
        isOpen={showCancelDialog}
        close={() => {
          setShowCancelDialog(false);
        }}
        referenceId={reference}
        refetchQuotationLists={refetchQuotationLists}
      />
      <ExtendQuoteDialog
        isOpen={showExtendDialog}
        close={() => {
          setShowExtendDialog(false);
        }}
        reference={quotation}
        refetchQuotationLists={refetchQuotationLists}
      />
      <QuotationDuplicateDialog
        isOpen={showDuplicateDialog}
        close={() => {
          setShowDuplicateDialog(false);
        }}
        referenceId={reference}
      />
      <EditScheduleDialog
        quotation={reference}
        isOpen={showEditScheduleDialog}
        close={() => {
          setShowEditScheduleDialog(false);
        }}
        refetchQuotationLists={refetchQuotationLists}
        draftId={draftId}
      />

      <Dropdown
        options={kebabMenuOptions}
        placeholder={intl.formatMessage({
          id: "quote.quotation.manageQuote",
          defaultMessage: "Manage quote",
        })}
        variant="secondary-color"
        textTertiary={false}
        expandAbove={isDetailDrawerOpen}
      />

      {showRevalidateQuoteButton && (
        <Button
          label={intl.formatMessage({
            id: "quote.quotation.revalidateQuote",
            defaultMessage: "Revalidate quote",
          })}
          onClick={handleClickRevalidate}
          disabled={isConfirmingQuotation || isLoadingQuotationLists}
        />
      )}

      {showExtendQuoteButton && (
        <Button
          label={intl.formatMessage({
            id: "general.extend",
            defaultMessage: "Extend",
          })}
          onClick={() => setShowExtendDialog(true)}
        />
      )}
    </>
  );
}
